import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { rememberRoute } from '@/lib/lastRoute'
import { useRealtime } from '@/hooks/useRealtime'
import { useAuth } from '@/context/AuthContext'
import { cn } from '@/lib/utils'
import { MODULE_DISABLED_EVENT, moduleForPath, type ModuleDisabledDetail } from '@/lib/modules'
import { TWO_FACTOR_REQUIRED_EVENT } from '@/lib/api'
import type { ModuleName } from '@/types/api'
import { SidebarInset, SidebarProvider, TooltipProvider } from '@/components/ui'
import { OfflineQueueBar } from './OfflineQueueBar'
import { UpdatePrompt } from './UpdatePrompt'
import { fullVersion, shortVersion } from '@/lib/version'
import { ImpersonationBanner } from './ImpersonationBanner'
import { ErrorBoundary } from './ErrorBoundary'
import { ModuleDisabled } from './ModuleDisabled'
import { NoPlantNotice } from './PlantAccessNotices'
import { blockedWithoutPlants, hasNoPlants } from '@/lib/plantAccess'
import { AppHeader } from './shell/AppHeader'
import { AppSidebar } from './shell/AppSidebar'
import { MobileTabBar } from './shell/MobileTabBar'
import { SearchPalette } from './shell/SearchPalette'
import { quickActions, visibleNavigation } from './shell/navigation'
import { isPaletteShortcut } from './shell/shortcut'

/*
 * The frame around every signed-in screen.
 *
 * Two navigations on purpose. A desk gets the grouped rail on the left, a
 * header with the page's name, search, "+ Novo", the bell and the account.
 * A phone keeps a bottom bar, because the primary user is holding it
 * one-handed next to a machine and the reachable part of that screen is the
 * bottom third; the rail is still there, as a sheet behind the header's menu
 * button, for the destinations four tabs have no room for.
 *
 * The pieces live in ./shell — what the navigation offers and to whom in
 * `navigation.ts`, search in `searchModel.ts` — so the rules are tested as
 * plain functions and this file only puts them together.
 */

/**
 * Whether the rail was left expanded last time.
 *
 * shadcn's `SidebarProvider` writes `sidebar_state` on every toggle but never
 * reads it back — in the Next.js template the cookie is read on the server and
 * handed in as `defaultOpen`. There is no server doing that in a Vite SPA, so
 * the write went nowhere and a collapsed rail sprang back open on every reload.
 * This is the missing half.
 */
function storedSidebarOpen(): boolean {
  const match = document.cookie.match(/(?:^|;\s*)sidebar_state=(true|false)/)

  return match ? match[1] === 'true' : true
}

/*
 * The rail's widths come from index.css, where they shrink with a fine
 * pointer: 64px collapsed on touch, which fits a 44px target plus the menu's
 * padding, and 48px with a mouse, which fits shadcn's own 32px row.
 */
const RAIL_WIDTHS = {
  '--sidebar-width': 'var(--shell-rail)',
  '--sidebar-width-icon': 'var(--shell-rail-icon)',
} as CSSProperties

export function AppShell() {
  const { user, impersonation, refreshUser } = useAuth()
  const location = useLocation()

  /*
   * A module switched off while this person was signed in.
   *
   * Their session still says it is on — the owner changed it a minute ago —
   * so the navigation offers it and the first request into it comes back 403
   * with the module named. The interceptor announces that; this is where it
   * lands. Two things happen: the session is re-read, so the navigation and
   * the route guards catch up, and if the refusal was about the page on
   * screen, the page is replaced by the explanation straight away instead of
   * by whatever error state it would have drawn.
   *
   * "About the page on screen" is the path check. Home reads submissions as
   * well, and a refused tile there must not replace the whole home screen.
   */
  const [refused, setRefused] = useState<{ module: ModuleName; path: string } | null>(null)
  const lastRefresh = useRef(0)

  useEffect(() => {
    function onRefused(event: Event) {
      const { module } = (event as CustomEvent<ModuleDisabledDetail>).detail

      setRefused({ module, path: window.location.pathname })

      /*
       * Once per burst. A dashboard fires three or four requests at once and
       * every one of them is refused; one re-read answers all of them.
       */
      if (Date.now() - lastRefresh.current > 10_000) {
        lastRefresh.current = Date.now()
        refreshUser().catch(() => {
          // The guard still shows the explanation; a failed re-read only
          // means the navigation catches up on the next sign-in instead.
        })
      }
    }

    window.addEventListener(MODULE_DISABLED_EVENT, onRefused)

    /*
     * Two-factor newly required: the same once-per-burst re-read, so the
     * session learns it and the Administracija gate takes over the screen.
     */
    const onTwoFactor = () => {
      if (Date.now() - lastRefresh.current > 10_000) {
        lastRefresh.current = Date.now()
        refreshUser().catch(() => {})
      }
    }

    window.addEventListener(TWO_FACTOR_REQUIRED_EVENT, onTwoFactor)

    return () => {
      window.removeEventListener(MODULE_DISABLED_EVENT, onRefused)
      window.removeEventListener(TWO_FACTOR_REQUIRED_EVENT, onTwoFactor)
    }
  }, [refreshUser])

  const refusedHere =
    refused &&
    refused.path === location.pathname &&
    moduleForPath(location.pathname) === refused.module
      ? refused.module
      : null

  /*
   * Signed in with no plant at all.
   *
   * Every list is cut to the reader's plants, so for this person every screen
   * would be empty — and nothing on an empty screen says why. The screens that
   * are nothing but plant data are replaced by the notice; the ones that still
   * mean something (the profile, the guide, administration) keep it as a strip
   * on top. See `blockedWithoutPlants` for which are which.
   */
  const withoutPlants = hasNoPlants(user)

  // Recorded on every navigation so that reopening the app — or a session
  // expiring overnight — returns the user to the screen they were working on.
  useEffect(() => {
    rememberRoute(location.pathname + location.search)
  }, [location.pathname, location.search])

  /*
   * Live updates over Reverb, scoped to what this role may hear.
   *
   * Mounted here rather than per page: a supervisor's queue should update while
   * they are reading a submission, and the bell should light up wherever they
   * are. Does nothing if Reverb is not configured or not running — polling
   * still covers it.
   */
  useRealtime()

  const nav = useMemo(() => visibleNavigation(user, Boolean(impersonation)), [user, impersonation])
  const actions = useMemo(() => quickActions(user), [user])

  /*
   * Search, from anywhere: Ctrl+K, or ⌘K on a Mac.
   *
   * On the window rather than on a focused element, so it works with the
   * caret in a form field as well — that is when somebody most often wants to
   * jump somewhere else. The same keys close it again.
   */
  const [searchOpen, setSearchOpen] = useState(false)

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (!isPaletteShortcut(event)) return

      event.preventDefault()
      setSearchOpen((open) => !open)
    }

    window.addEventListener('keydown', onKeyDown)

    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  return (
    /*
     * TooltipProvider groups the rail's tooltips: once one has been shown,
     * moving to the next item shows it immediately instead of waiting out the
     * delay again. Without it each item is its own island and scanning a
     * collapsed rail means a pause per icon.
     */
    <TooltipProvider>
      <SidebarProvider defaultOpen={storedSidebarOpen()} style={RAIL_WIDTHS}>
        <AppSidebar nav={nav} />

        {/* Transparent, so the body's ambient gradient reads through the whole
            content column instead of being covered by an opaque panel. */}
        <SidebarInset className="min-w-0 bg-transparent">
          {/* Connection and queue status. Renders nothing when online with an
              empty queue, so it costs no space in the normal case. */}
          <OfflineQueueBar />

          {/*
            One sticky stack, not two.

            The support banner has to stay on screen — that is the whole point
            of it — and so does the header. Two independently sticky elements
            both pinned to `top-0` would occupy the same strip, with the taller
            z-index simply covering the other. Sticking the *container* keeps
            them in document order and pins them as one block.
          */}
          <div className="sticky top-0 z-20">
            <ImpersonationBanner />
            <AppHeader actions={actions} onSearch={() => setSearchOpen(true)} />
          </div>

          {/*
            A sunken canvas so the cards read as raised surfaces rather than as
            outlines drawn on the same sheet. `p-page` is the density token:
            16–24px on touch, 20px at a desk.

            The bottom padding on a phone clears the floating tab bar and the
            safe-area inset, so the last row of a list is never trapped behind
            it. It used to clear two floating buttons stacked above the bar as
            well — 13rem of it — which is gone with the buttons. Nothing floats
            over the content now, on any screen.
          */}
          <main className="p-page min-w-0 flex-1 pb-[calc(6rem+env(safe-area-inset-bottom))] lg:pb-page">
            {/*
              Scoped to the routed page, not the whole app: a crash in one screen
              leaves the navigation and the notification bell working, and keying
              it on the path means navigating away clears it.
            */}
            <ErrorBoundary resetKey={location.pathname}>
              {/*
                Keyed on the path so each screen fades and lifts in as the tab
                bar's pill slides — the two motions read as one gesture.

                Keying costs nothing here: a route change unmounts the old page
                regardless, and a change of query string alone (?otvorene=true)
                leaves the key untouched, so filtering a list does not re-animate
                or lose its state.

                `data-slot="page"` is what index.css keys the desk-sized page
                title on.
              */}
              <div
                key={location.pathname}
                data-slot="page"
                className="animate-in fade-in-0 slide-in-from-bottom-2 duration-300 motion-reduce:animate-none"
              >
                {refusedHere ? (
                  <ModuleDisabled module={refusedHere} />
                ) : withoutPlants && blockedWithoutPlants(location.pathname) ? (
                  <NoPlantNotice />
                ) : (
                  <>
                    {withoutPlants && <NoPlantNotice compact />}
                    <Outlet />
                  </>
                )}
              </div>
            </ErrorBoundary>
          </main>

          {/*
            "A new version is out." Mounted beside the footer rather than inside
            it because the footer is `lg:flex` — desktop only — and the tab that
            sits open across a deploy is overwhelmingly a phone. It is `fixed` and
            renders nothing until a new service worker has installed, so its
            position in this tree costs nothing either way.
          */}
          <UpdatePrompt />

          {/*
            Attribution, always on screen.

            `sticky bottom-0` rather than `fixed`: the page still scrolls
            normally, but the bar stays glued to the bottom of the viewport
            without ever needing to be scrolled to — and because it is inside
            SidebarInset it spans the content column only, instead of sliding
            under the sidebar the way a fixed element would.

            Desktop only, and one line of small type: it is a credit, and it was
            taking a row of every screen's height to say so. On a phone the
            bottom edge belongs to the tab bar; there the credit lives on the
            login and account screens.
          */}
          <footer
            className={cn(
              'sticky bottom-0 z-20 hidden items-center justify-between gap-3 px-4 py-1.5 lg:flex',
              'border-t border-black/5 dark:border-white/10',
              'bg-background/85 supports-[backdrop-filter]:bg-background/70',
              'text-muted-foreground text-[0.6875rem] backdrop-blur-xl backdrop-saturate-150',
            )}
          >
            {/*
              The signed-in user's own company, never a hard-coded one.

              This said "Livprom d.o.o." while the product was one company's
              internal tool. On a shared installation it credits a stranger's
              company at the foot of every other customer's screen.

              Falls back to the product alone rather than to a placeholder, since
              an owner's own console has no customer to credit.
            */}
            <span>
              KaProLean
              {user?.company?.name && ` · © ${new Date().getFullYear()} ${user.company.name}`}

              {/*
                Which build this is, in the quietest place it can still be read.

                `select-all` so one click takes the whole token — this gets read
                down a phone line or pasted into a message, and a half-selected
                commit is worse than none. Monospace for the same reason: a `0`
                and an `O` have to be tellable apart out loud.
              */}
              {' · '}
              <span className="cursor-help font-mono select-all" title={fullVersion()}>
                {shortVersion()}
              </span>
            </span>

            <span>
              Softver:{' '}
              {/* noopener/noreferrer: without them the opened tab gets a handle
                  back on this one through window.opener. */}
              <a
                href="https://kala.rs"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-foreground font-medium underline-offset-4 hover:underline"
              >
                Kala Consulting
              </a>{' '}
              &{' '}
              <a
                href="https://yanoka.com"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-foreground font-medium underline-offset-4 hover:underline"
              >
                Yanoka
              </a>
            </span>
          </footer>
        </SidebarInset>

        <MobileTabBar tabs={nav.tabs} actions={actions} />

        <SearchPalette open={searchOpen} onOpenChange={setSearchOpen} nav={nav} actions={actions} />
      </SidebarProvider>
    </TooltipProvider>
  )
}
